import { Stack } from '@daily/shared/components/Stack';
import { Text } from '@daily/shared/components/Text';
import { useResize } from '@daily/shared/hooks/useResize';
import { useParticipantIds } from '@daily-co/daily-react-hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCallConfig } from '../../hooks/useCallConfig';
import { useResizeObserver } from '../../hooks/useResizeObserver';
import { RecordingState } from '../Call/RecordingState';
import { LiveBadge } from './LiveBadge';
import { PictureInPictureButton } from './PictureInPictureButton';
import { SettingsButton } from './SettingsButton';
import { TopbarAction } from './TopbarAction';

export const Topbar: React.FC = () => {
  const { t } = useTranslation();
  const {
    broadcast,
    broadcastRole,
    enablePeopleUI,
    enablePIPUI,
    poweredByDaily,
  } = useCallConfig();
  const topbarRef = useRef<HTMLDivElement>(null);
  const [supportsPIP, setSupportsPIP] = useState(false);

  const showParticipantCount = useMemo(
    () => broadcastRole !== 'attendee' || enablePeopleUI,
    [broadcastRole, enablePeopleUI]
  );

  const allIds = useParticipantIds();
  const ownerIds = useParticipantIds({ filter: 'owner' });

  /**
   * In OOB calls, owners should always see the full count, not just visible
   * participants. Attendees can see the full count if chat is enabled. Otherwise
   * we don't share participant info.
   * In non-OOB calls, show the usual participant count.
   */
  const count = useMemo(
    () =>
      broadcastRole === 'owner' ||
      (broadcastRole === 'attendee' && enablePeopleUI) ||
      !broadcast
        ? allIds.length
        : ownerIds.length,
    [allIds.length, broadcast, broadcastRole, enablePeopleUI, ownerIds.length]
  );

  const peopleInCall = useMemo(
    () => (
      <Text>
        {count <= 1
          ? t('people.waitingForOtherParticipants')
          : t('people.withCount', { count })}
      </Text>
    ),
    [count, t]
  );

  /**
   * Recalculates and sets the --header-height CSS variable.
   */
  const updateCSSHeaderHeight = useCallback(() => {
    if (!topbarRef.current) return;
    const { height, top } = topbarRef.current.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height + top}px`);
  }, []);
  /**
   * Set up ResizeObserver to determine when Topbar shifts position.
   */
  useResizeObserver(
    topbarRef,
    useCallback(() => {
      updateCSSHeaderHeight();
    }, [updateCSSHeaderHeight])
  );
  useResize(updateCSSHeaderHeight, [poweredByDaily, updateCSSHeaderHeight]);

  useEffect(() => {
    // TODO: Figure out why pictureInPicture types are missing in `next build`
    // @ts-ignore
    setSupportsPIP(document.pictureInPictureEnabled);
  }, []);

  return (
    <div ref={topbarRef} className="topbar">
      <Stack align="center" justify="space-between" horizontal>
        <Stack align="center" horizontal>
          <SettingsButton />
          {showParticipantCount && peopleInCall}
          <LiveBadge />
        </Stack>
        <RecordingState />
        <div className="action">
          <TopbarAction />
          {enablePIPUI && supportsPIP && <PictureInPictureButton />}
        </div>
      </Stack>
      <style jsx>{`
        .topbar {
          background: var(--card-bg);
          border-bottom: 1px solid var(--card-border);
          flex: none;
          padding: 4px 8px;
          width: 100vw;
          z-index: var(--zindex-tray);
        }
        .action {
          display: flex;
        }
      `}</style>
    </div>
  );
};
